<template>
    <div class="quickpay-pdf-viewer">
        <div class="quickpay-pdf-viewer__header">
            <div class="quickpay-pdf-viewer__header-content">
                <p
                    v-if="pdfType"
                    class="quickpay-pdf-viewer__type typographies-paragraph-large-bold"
                >
                    {{ pdfType }}
                </p>
                <div class="quickpay-pdf-viewer__title-container">
                    <p
                        v-if="document.number"
                        class="quickpay-pdf-viewer__title typographies-paragraph-medium-regular"
                    >
                        N° {{ document.number }}
                    </p>
                    <TagStatus :id="pdfStatus.id" :label="pdfStatus.label" size="small" />
                </div>
            </div>
            <CompassPlainButton
                :label="$t.__('common.labels.download')"
                leftIcon="download"
                variant="neutral"
                :loading="isDownloading"
                :disabled="loading"
                @click="downloadPdf"
            />
        </div>
        <div v-if="loading && !generationError" class="quickpay-pdf-viewer__loader">
            <CompassLoader />
        </div>
        <div v-else-if="generationError || renderError" />
        <PDFViewerNative v-else-if="isNative && shouldShowPdf" :src="src" />
        <PDFViewer
            v-else-if="shouldShowPdf"
            :src="src"
            :showZoom="true"
            :showPagination="true"
            :showExternalLink="true"
            :expired="document.expired"
            @pdf-error="onPdfError"
        />
    </div>
</template>

<script>
import TagStatus from 'vcomponents/common/TagStatus/TagStatus.vue';
import PDFViewer from 'vcomponents/common/pdf/PDFViewer.vue';
import PDFViewerNative from 'vcomponents/common/pdf/PDFViewerNative.vue';
import { trackDownload } from 'api/quickpay/quickpayTrackingService.js';

export default {
    components: {
        PDFViewer,
        PDFViewerNative,
        TagStatus,
    },
    props: {
        src: {
            type: String,
            required: true,
        },
        document: {
            type: Object,
            required: true,
        },
        hash: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isDownloading: false,
            isNative: false,
            loading: true,
            pdfReady: false,
            pingAttempts: 0,
            maxPingAttempts: 20,
            pingInterval: null,
            generationError: false,
            renderError: false,
        };
    },
    computed: {
        pdfType() {
            return this.$t.__(`quickpay.docType.${this.document.doctype}`);
        },
        pdfStatus() {
            return {
                id: this.document.step,
                label: this.$t.__(`quickpay.status.${this.document.step}`),
            };
        },
        fileName() {
            return this.document.number
                ? `${this.pdfType}_${this.document.number}.pdf`
                : `${this.pdfType}.pdf`;
        },
        shouldShowPdf() {
            return this.pdfReady && !this.loading;
        },
    },
    mounted() {
        this.startPdfPolling();
    },
    unmounted() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
        }
    },
    methods: {
        startPdfPolling() {
            this.pingPdf();
            this.pingInterval = setInterval(() => {
                if (this.loading && this.pingAttempts < this.maxPingAttempts) {
                    this.pingPdf();
                } else if (this.pingAttempts >= this.maxPingAttempts) {
                    this.loading = false;
                    this.generationError = true;
                    clearInterval(this.pingInterval);
                } else {
                    clearInterval(this.pingInterval);
                }
            }, 3000);
        },
        pingPdf() {
            this.pingAttempts++;
            SellsyJS.WebServiceCaller.post(
                `/quickpay/pdf`,
                {},
                { hash: this.hash },
                (response) => {
                    if (response.pdfGenerated && this.loading) {
                        this.pdfReadyCallback();
                    }
                },
            );
        },
        pdfReadyCallback() {
            this.loading = false;
            this.pdfReady = true;
            if (this.pingInterval) {
                clearInterval(this.pingInterval);
            }
        },
        onPdfError() {
            this.loading = false;
            this.renderError = true;
        },
        async downloadPdf() {
            if (this.isDownloading) {
                return;
            }
            this.isDownloading = true;
            try {
                const response = await fetch(this.src);
                if (!response.ok) {
                    throw new Error(this.$t.__('quickpay.error.download'));
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                trackDownload(this.document.doctype, this.document.id, this.document.corpid);
            } finally {
                this.isDownloading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-pdf-viewer {
    max-width: 645px;
    width: 100%;
    height: calc(100% - 68px);
    display: flex;
    flex-direction: column;
    border-radius: var(--spacing-1) var(--spacing-1) 0 0;
    box-shadow: var(--elevation-1);
    margin: 64px 0 0;
    overflow: hidden;

    @media (min-width: 1441px) {
        max-width: 800px;
        height: calc(100% - 64px);
    }

    @media (max-width: 1023px) {
        display: none;
    }

    &__header {
        height: 80px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-3) var(--spacing-4);
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-1) var(--spacing-1) 0 0;
    }
    &__header-content {
        display: flex;
        flex-direction: column;
    }
    &__title-container {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
    }
    &__loader {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    &__type,
    &__title {
        color: var(--colors-common-text-standard-default);
        margin: 0;
    }
}
</style>
